import { useState } from 'react';
import axios from 'axios';
import { X, Phone, Mail } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { business } from '../../mock';
import PhotoUpload from './PhotoUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteModal({ open, onClose }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', business_name: '', message: '',
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.message) {
      toast({ title: 'Please complete name, phone, address and message', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        address: form.address,
        business_name: form.business_name || null,
        message: form.message,
        source: 'quote_modal',
        agreed_terms: true,
        photos: photos.map(({ storage_path, filename, content_type, size_bytes }) => ({ storage_path, filename, content_type, size_bytes })),
      });
      toast({ title: 'Thanks! We\'ll be in touch shortly with your free no obligation quote.' });
      setForm({ name: '', phone: '', email: '', address: '', business_name: '', message: '' });
      setPhotos([]);
      onClose();
    } catch {
      toast({
        title: 'Something went wrong',
        description: 'Please try again or call us directly.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-modal-title"
      data-testid="quote-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="h-1 bg-gradient-to-r from-[#1B6E37] via-[#D98A00] to-[#B86F00]" />
        <div className="px-6 py-5 flex items-center justify-between border-b border-[#E2DAC5]">
          <div>
            <p className="section-label">Free Quote</p>
            <h3 id="quote-modal-title" className="text-xl font-extrabold text-[#0A2E18]" style={{ fontFamily: 'Inter, sans-serif' }}>
              Tell us about your property
            </h3>
          </div>
          <button onClick={onClose} className="text-[#0A2E18]/60 hover:text-[#0A2E18]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-3 bg-[#FAF8F2] flex items-center gap-4 text-sm border-b border-[#E2DAC5]">
          <a href={`tel:${business.phone}`} className="flex items-center gap-1 text-[#0A2E18] font-bold hover:text-[#1B6E37]">
            <Phone className="w-4 h-4" /> {business.phone}
          </a>
          <a href={`mailto:${business.email}`} className="flex items-center gap-1 text-[#212B20] hover:text-[#1B6E37]">
            <Mail className="w-4 h-4" /> {business.email}
          </a>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">CONTACT NAME *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">PHONE *</label>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">EMAIL</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">BUSINESS NAME</label>
            <input type="text" placeholder="Optional" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">JOB ADDRESS *</label>
            <input type="text" placeholder="Street, suburb, postcode" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">HOW CAN WE HELP? *</label>
            <textarea rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us about your lawn & garden needs..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20 resize-none" />
          </div>
          <PhotoUpload value={photos} onChange={setPhotos} variant="light" />
          <button type="submit" disabled={submitting} className="btn-gold w-full py-3 rounded-md disabled:opacity-60">
            {submitting ? 'Sending...' : 'Send Request'}
          </button>
        </form>
      </div>
    </div>
  );
}
